import type { Over } from '@dnd-kit/core'
import { Modifier } from '@dnd-kit/core'
import {
  IComputeItemsAfterDropResult,
  IDndItem,
  IGroupBoundsOffsets
} from './types'
import { RefObject } from 'react'
import { IDropState } from 'context/TimelineContext'

export const GAP_AFTER_PREFIX = 'gap-after-'

export const getGapAfterId = (afterLineIndex: number): string =>
  `${GAP_AFTER_PREFIX}${afterLineIndex}`

export type TParsedDropTarget =
  | { kind: 'line'; lineIndex: number }
  | { kind: 'gap'; afterLineIndex: number }

export const parseDropTarget = (
  over: Over | null | undefined
): TParsedDropTarget | null => {
  if (!over) return null
  const id = String(over.id)
  if (id.startsWith(GAP_AFTER_PREFIX)) {
    const n = Number(id.slice(GAP_AFTER_PREFIX.length))
    if (Number.isNaN(n)) return null
    return { kind: 'gap', afterLineIndex: n }
  }
  const lineIndex = typeof over.id === 'number' ? over.id : Number(over.id)
  if (Number.isNaN(lineIndex)) return null
  return { kind: 'line', lineIndex }
}

export const getSortedLineIndices = (items: IDndItem[]): number[] => {
  const unique = new Set<number>()
  items.forEach(item => unique.add(item.lineIndex))
  return [...unique].sort((a, b) => a - b)
}

export const shiftItemsForGapInsert = (
  items: IDndItem[],
  draggedIds: string | string[],
  insertAtLine: number
): IDndItem[] => {
  const skip = new Set(Array.isArray(draggedIds) ? draggedIds : [draggedIds])
  return items.map(item => {
    if (skip.has(item.id)) return item
    if (item.lineIndex >= insertAtLine) {
      return { ...item, lineIndex: item.lineIndex + 1 }
    }
    return item
  })
}

export const restrictToSpecificContainer = (
  containerId: string,
  groupOffsets?: IGroupBoundsOffsets | null | undefined
): Modifier => {
  return args => {
    const { transform, draggingNodeRect } = args

    if (!draggingNodeRect) {
      return transform
    }

    const container = document.getElementById(containerId)
    if (!container) {
      return transform
    }

    const containerRect = container.getBoundingClientRect()

    const groupLeft =
      draggingNodeRect.left + (groupOffsets?.leftOffsetFromActive ?? 0)
    const groupRight =
      draggingNodeRect.right + (groupOffsets?.rightOffsetFromActive ?? 0)

    return {
      ...transform,
      x: Math.min(
        Math.max(transform.x, containerRect.left - groupLeft),
        containerRect.right - groupRight
      ),
      y: Math.min(
        Math.max(transform.y, containerRect.top - draggingNodeRect.top),
        containerRect.bottom - draggingNodeRect.bottom
      )
    }
  }
}

export const groupByLineIndex = (items: IDndItem[]): IDndItem[][] => {
  const result: IDndItem[][] = []
  items.forEach(item => {
    if (!result[item.lineIndex]) {
      result[item.lineIndex] = []
    }
    result[item.lineIndex].push(item)
  })
  return result
}

export const calculateNewPositions = ({
  draggedItem,
  items,
  dropLineIndex,
  containerWidth,
  deltaX
}: {
  draggedItem: IDndItem
  items: IDndItem[]
  dropLineIndex: number
  containerWidth: number
  deltaX: number
}) => {
  const draggedItemLeft = Math.min(
    draggedItem.left + deltaX,
    containerWidth - draggedItem.width
  )

  const itemsInLine = items.filter(item => item.lineIndex === dropLineIndex)
  const nonDraggedItems = itemsInLine.filter(item => item.id !== draggedItem.id)

  const reorderedItems = [
    ...nonDraggedItems,
    { ...draggedItem, left: draggedItemLeft }
  ].sort((a, b) => a.left - b.left)

  const draggedIndex = reorderedItems.findIndex(
    item => item.id === draggedItem.id
  )
  const prevItemRightEdge =
    draggedIndex > 0
      ? reorderedItems[draggedIndex - 1].left +
        reorderedItems[draggedIndex - 1].width
      : 0
  const nextItemLeftEdge =
    draggedIndex < reorderedItems.length - 1
      ? reorderedItems[draggedIndex + 1].left
      : containerWidth

  const availableSpace = nextItemLeftEdge - prevItemRightEdge

  let finalLeftPosition = draggedItemLeft
  let leftShiftAmount = 0

  if (availableSpace < draggedItem.width) {
    finalLeftPosition = prevItemRightEdge
    leftShiftAmount = draggedItem.width - availableSpace
  } else {
    if (draggedItemLeft + draggedItem.width > nextItemLeftEdge) {
      finalLeftPosition = nextItemLeftEdge - draggedItem.width
    } else if (draggedItemLeft < prevItemRightEdge) {
      finalLeftPosition = prevItemRightEdge
    }
  }

  return items.map(item => {
    if (item.id === draggedItem.id) {
      return {
        ...item,
        lineIndex: dropLineIndex,
        left: finalLeftPosition
      }
    }

    const isItemInLine = item.lineIndex === dropLineIndex
    if (isItemInLine && item.left > draggedItemLeft) {
      return { ...item, left: item.left + leftShiftAmount }
    }

    return item
  })
}

const POSITION_EPSILON = 0.5

export const getDraggedGroup = (
  items: IDndItem[],
  draggedId: string,
  selectedIds?: string[]
): IDndItem[] => {
  const draggedItem = items.find(item => item.id === draggedId)
  if (!draggedItem) return []
  if (
    !selectedIds ||
    selectedIds.length <= 1 ||
    !selectedIds.includes(draggedId)
  ) {
    return [draggedItem]
  }
  const set = new Set(selectedIds)

  // all group members must share draggedItem lineIndex
  return items
    .filter(
      item => set.has(item.id) && item.lineIndex === draggedItem.lineIndex
    )
    .sort((a, b) => a.left - b.left)
}

// to check if selected items are attached or have gaps between them
export const isGroupContiguous = (group: IDndItem[]): boolean => {
  if (group.length <= 1) return true
  const sorted = group.toSorted((a, b) => a.left - b.left)
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    if (Math.abs(curr.left - (prev.left + prev.width)) > POSITION_EPSILON) {
      return false
    }
  }
  return true
}

export const getGroupBounds = (group: IDndItem[]) => {
  const sorted = group.toSorted((a, b) => a.left - b.left)
  const groupLeftEdge = sorted[0].left
  const lastItem = sorted[sorted.length - 1]
  const groupRightEdge = lastItem.left + lastItem.width
  return {
    groupLeftEdge,
    groupRightEdge,
    groupSpanLength: groupRightEdge - groupLeftEdge
  }
}

interface IGroupResult {
  items: IDndItem[]
  promoteToNewLine: boolean
}

/**
 * Calculates positions when a group of items is dropped onto a line.
 * It inserts the group and "cascades" (pushes) only the items that
 * would overlap with the group or previously pushed items.
 */
const calculateGroupCascadePositions = ({
  group,
  items,
  dropLineIndex,
  containerWidth,
  deltaX
}: {
  group: IDndItem[]
  items: IDndItem[]
  dropLineIndex: number
  containerWidth: number
  deltaX: number
}): IGroupResult => {
  const groupIds = new Set(group.map(item => item.id))
  const { groupLeftEdge, groupSpanLength } = getGroupBounds(group)

  // 1. Calculate the raw target position based on drag delta, clamped to container
  const rawTargetLeft = Math.max(
    0,
    Math.min(groupLeftEdge + deltaX, containerWidth - groupSpanLength)
  )

  // 2. Isolate and sort other items on the target line
  const lineSiblings = items
    .filter(item => item.lineIndex === dropLineIndex && !groupIds.has(item.id))
    .sort((a, b) => a.left - b.left)

  // 3. Find insertion index (where the group wants to land)
  const insertionIndex = lineSiblings.findIndex(
    item => item.left >= rawTargetLeft
  )
  const finalIdx = insertionIndex === -1 ? lineSiblings.length : insertionIndex

  // 4. Determine bounds based on surrounding items
  const prevItem = lineSiblings[finalIdx - 1]
  const nextItem = lineSiblings[finalIdx]

  const prevRight = prevItem ? prevItem.left + prevItem.width : 0
  const nextLeft = nextItem ? nextItem.left : containerWidth
  const availableSpace = nextLeft - prevRight

  // 5. Finalize group position:
  // If it fits in the gap, respect the drag position.
  // If not, snap to the right of the previous item.
  const finalGroupLeft =
    availableSpace >= groupSpanLength
      ? Math.max(prevRight, Math.min(rawTargetLeft, nextLeft - groupSpanLength))
      : prevRight

  const groupShift = finalGroupLeft - groupLeftEdge
  const groupRightBoundary = finalGroupLeft + groupSpanLength

  // 6. Chain Cascade: Calculate new positions for items to the right of the group
  const updatedPositions = new Map<string, number>()
  let cursor = groupRightBoundary

  for (let i = finalIdx; i < lineSiblings.length; i++) {
    const item = lineSiblings[i]
    if (item.left >= cursor) break // Stop cascading once we find an item that already clears the cursor

    updatedPositions.set(item.id, cursor)
    cursor += item.width
  }

  // 7. Map back to the full items list
  const finalItems = items.map(item => {
    // Is it part of the dragging group?
    if (groupIds.has(item.id)) {
      return {
        ...item,
        lineIndex: dropLineIndex,
        left: item.left + groupShift
      }
    }

    // Was it pushed by the cascade?
    const newLeft = updatedPositions.get(item.id)
    if (newLeft !== undefined) {
      return { ...item, left: newLeft }
    }

    return item
  })

  return { items: finalItems, promoteToNewLine: false }
}

// Tries to place the (non-contiguous) group preserving its internal gaps.
// If any group member would overlap a non-group item on dropLineIndex,
// returns `promoteToNewLine: true` so the caller can re-run on a fresh line.
const calculateGroupFitPositions = ({
  group,
  items,
  dropLineIndex,
  containerWidth,
  deltaX
}: {
  group: IDndItem[]
  items: IDndItem[]
  dropLineIndex: number
  containerWidth: number
  deltaX: number
}): IGroupResult => {
  const groupIds = new Set(group.map(g => g.id))
  const { groupLeftEdge, groupSpanLength } = getGroupBounds(group)

  const draggingItemsLeftPosition = Math.min(
    Math.max(groupLeftEdge + deltaX, 0),
    containerWidth - groupSpanLength
  )
  const groupShift = draggingItemsLeftPosition - groupLeftEdge

  const hasOverlap = getHasOverlapDuringDrag({
    items,
    lineIndex: dropLineIndex,
    deltaX: groupShift,
    group
  })

  if (hasOverlap) {
    return { items, promoteToNewLine: true }
  }

  return {
    items: items.map(item => {
      if (groupIds.has(item.id)) {
        return {
          ...item,
          lineIndex: dropLineIndex,
          left: item.left + groupShift
        }
      }
      return item
    }),
    promoteToNewLine: false
  }
}

// `didPromoteToNewLine` is true only for the non-contiguous-group-on-line
// path where overlap forced a fresh line via `shiftItemsForGapInsert`. The
// dragged item's `lineIndex` matches `target.lineIndex` either way (it's
// the OTHER items that shift down), so callers can't infer promotion from
// the items alone.

export const computeItemsAfterDrop = ({
  draggedItem,
  items,
  target,
  containerWidth,
  deltaX,
  selectedIds
}: {
  draggedItem: IDndItem
  items: IDndItem[]
  target: TParsedDropTarget
  containerWidth: number
  deltaX: number
  selectedIds?: string[]
}): IComputeItemsAfterDropResult => {
  const group = getDraggedGroup(items, draggedItem.id, selectedIds)

  if (group.length <= 1) {
    if (target.kind === 'line') {
      return {
        items: calculateNewPositions({
          draggedItem,
          items,
          dropLineIndex: target.lineIndex,
          containerWidth,
          deltaX
        }),
        didPromoteToNewLine: false
      }
    }
    const insertAtLine = target.afterLineIndex + 1
    const baseItems = shiftItemsForGapInsert(
      items,
      draggedItem.id,
      insertAtLine
    )
    return {
      items: calculateNewPositions({
        draggedItem,
        items: baseItems,
        dropLineIndex: insertAtLine,
        containerWidth,
        deltaX
      }),
      didPromoteToNewLine: false
    }
  }

  const groupIds = group.map(g => g.id)
  const isContiguous = isGroupContiguous(group)

  if (target.kind === 'gap') {
    const insertAtLine = target.afterLineIndex + 1
    const baseItems = shiftItemsForGapInsert(items, groupIds, insertAtLine)
    return {
      items: calculateGroupFitPositions({
        group,
        items: baseItems,
        dropLineIndex: insertAtLine,
        containerWidth,
        deltaX
      }).items,
      didPromoteToNewLine: false
    }
  }

  if (isContiguous) {
    return {
      items: calculateGroupCascadePositions({
        group,
        items,
        dropLineIndex: target.lineIndex,
        containerWidth,
        deltaX
      }).items,
      didPromoteToNewLine: false
    }
  }

  // Non-contiguous group dropped on an existing line: try to fit, else
  // create a fresh line above the drop target and place the group there.
  const fit = calculateGroupFitPositions({
    group,
    items,
    dropLineIndex: target.lineIndex,
    containerWidth,
    deltaX
  })
  if (!fit.promoteToNewLine) {
    return { items: fit.items, didPromoteToNewLine: false }
  }

  const baseItems = shiftItemsForGapInsert(items, groupIds, target.lineIndex)
  return {
    items: calculateGroupFitPositions({
      group,
      items: baseItems,
      dropLineIndex: target.lineIndex,
      containerWidth,
      deltaX
    }).items,
    didPromoteToNewLine: true
  }
}

export const updateDropIndicators = ({
  containerRef,
  left,
  width,
  lineIndex
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
  left: number
  width: number
  lineIndex: number
}) => {
  if (!containerRef.current) return
  const dropIndicators =
    containerRef.current.querySelectorAll('.drop-indicator')
  dropIndicators.forEach(indicator => {
    const indicatorEl = indicator as HTMLElement // fix typescript type error
    const isDropLine = indicatorEl.dataset.lineIndex === lineIndex.toString()
    if (isDropLine) {
      indicatorEl.style.display = 'block'
      indicatorEl.style.left = `${left}px`
      indicatorEl.style.width = `${width}px`
    } else {
      indicatorEl.style.display = 'none'
    }
  })
}

export const hideDropIndicators = (
  containerRef: React.RefObject<HTMLDivElement | null>
) => {
  if (!containerRef.current) return
  const dropIndicators =
    containerRef.current.querySelectorAll('.drop-indicator')
  dropIndicators.forEach(indicator => {
    const indicatorEl = indicator as HTMLElement // fix typescript type error
    indicatorEl.style.display = 'none'
  })
}

export const getHasOverlapDuringDrag = ({
  items,
  lineIndex,
  deltaX,
  draggedItem,
  group
}: {
  items: IDndItem[]
  lineIndex: number
  deltaX: number
  draggedItem?: IDndItem
  group?: IDndItem[]
}): boolean => {
  const movingMembers =
    !!group && group.length > 1 ? group : draggedItem ? [draggedItem] : []

  if (!movingMembers?.length) return false

  const movingIds = new Set(movingMembers.map(m => m.id))

  const itemsInLine = items.filter(
    item => item.lineIndex === lineIndex && !movingIds.has(item.id)
  )

  return movingMembers.some(member => {
    const left = member.left + deltaX
    const right = left + member.width
    return itemsInLine.some(other => {
      const otherRight = other.left + other.width
      return left < otherRight && right > other.left
    })
  })
}
export const findItemEl = (
  id: string,
  containerRef: RefObject<HTMLDivElement | null>
): HTMLElement | null => {
  if (!containerRef.current) return null
  return containerRef.current.querySelector<HTMLElement>(`[data-id="${id}"]`)
}

export const applyGroupDragTransform = ({
  containerRef,
  group,
  activeId,
  deltaX,
  deltaY
}: {
  containerRef: RefObject<HTMLDivElement | null>
  group: IDndItem[]
  activeId: string | number
  deltaX: number
  deltaY: number
}) => {
  group.forEach(member => {
    if (member.id === activeId) return
    const el = findItemEl(member.id, containerRef)
    if (!el) return
    el.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0)`
    el.style.zIndex = '10'
    el.style.pointerEvents = 'none'
  })
}

export const clearGroupDragTransform = (
  containerRef: RefObject<HTMLDivElement | null>,
  group: IDndItem[],
  activeId: string | number
) => {
  group.forEach(member => {
    if (member.id === activeId) return
    const el = findItemEl(member.id, containerRef)
    if (!el) return
    el.style.transform = ''
    el.style.zIndex = ''
    el.style.pointerEvents = ''
  })
}

// Lets `MultiLineDND`'s `BetweenLinesGap` strips highlight during the
// outer (sidebar / OS-files) drag too. The outer drag's resolved gap target
// sits in `dropState.targetGapAfterLineIndex` (-1 = topmost gap).
export const resolveExternalGapState = (
  afterLineIndex: number,
  dropState?: IDropState
) => {
  if (!dropState) {
    return { isDragActive: false, isOver: false }
  }
  return {
    isDragActive: dropState.isDragOver,
    isOver:
      dropState.isDragOver &&
      dropState.targetGapAfterLineIndex === afterLineIndex
  }
}
